import { Typography } from 'antd';
import { useState } from 'react';
import { OfferedItemLookup } from './useOfferedItemsDataSource';

const { Text } = Typography;

function highlightMatch(text: string, query: string) {
  if (!query) return <>{text}</>;

  const safeQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(safeQuery, 'ig');

  const result: React.ReactNode[] = [];
  let lastIndex = 0;

  for (const match of Array.from(text.matchAll(regex))) {
    const start = match.index ?? 0;

    if (lastIndex < start) {
      const slice = text.slice(lastIndex, start).replace(/ /g, '\u00A0');
      result.push(<span key={`text-${lastIndex}`}>{slice}</span>);
    }

    result.push(
      <Text strong key={`match-${start}`}>
        {match[0]}
      </Text>,
    );

    lastIndex = start + match[0].length;
  }

  if (lastIndex < text.length) {
    const slice = text.slice(lastIndex).replace(/ /g, '\u00A0');
    result.push(<span key={`end-${lastIndex}`}>{slice}</span>);
  }

  return <>{result}</>;
}

export type OfferedItemSearchOptionType =
  | 'category'
  | 'subCategory'
  | 'subCategory2'
  | 'item';

export type OfferedItemSearchOption = {
  value: string;
  label: React.ReactNode;
  type: OfferedItemSearchOptionType;
};

export function useSearchOfferedItemsOptions(items: OfferedItemLookup[]) {
  const [searchValue, setSearchValue] = useState('');
  const [options, setOptions] = useState<OfferedItemSearchOption[]>([]);

  const handleSearch = (val: string) => {
    setSearchValue(val);
    if (!val) {
      setOptions([]);
      return;
    }
    const lower = val.toLowerCase();
    const matches: OfferedItemSearchOption[] = [];

    items
      .filter(
        (i, idx, arr) =>
          arr.findIndex((j) => j.category === i.category) === idx,
      )
      .filter((i) => i.category.toLowerCase().includes(lower))
      .forEach((i) =>
        matches.push({
          value: i.category,
          label: <>{highlightMatch(i.category, val)}</>,
          type: 'category',
        }),
      );

    items
      .filter(
        (i, idx, arr) =>
          arr.findIndex((j) => j.subcategory === i.subcategory) === idx,
      )
      .filter((i) => i.subcategory?.toLowerCase().includes(lower))
      .forEach((i) =>
        matches.push({
          value: i.subcategory!,
          label: (
            <>
              {highlightMatch(i.category, val)} /{' '}
              {highlightMatch(i.subcategory!, val)}
            </>
          ),
          type: 'subCategory',
        }),
      );

    items
      .filter(
        (i, idx, arr) =>
          arr.findIndex((j) => j.subcategory2 === i.subcategory2) === idx,
      )
      .filter((i) => i.subcategory2?.toLowerCase().includes(lower))
      .forEach((i) =>
        matches.push({
          value: i.subcategory2!,
          label: (
            <>
              {highlightMatch(i.category, val)} /{' '}
              {highlightMatch(i.subcategory!, val)} /{' '}
              {highlightMatch(i.subcategory2!, val)}
            </>
          ),
          type: 'subCategory2',
        }),
      );

    items
      .filter((i) => i.name.toLowerCase().includes(lower))
      .forEach((i) =>
        matches.push({
          value: i.id,
          label: (
            <>
              {highlightMatch(i.category, val)}
              {i.subcategory && <> / {highlightMatch(i.subcategory, val)}</>}
              {i.subcategory2 && <> / {highlightMatch(i.subcategory2, val)}</>}
              <> / {highlightMatch(i.name, val)}</>
            </>
          ),
          type: 'item',
        }),
      );

    setOptions(matches);
  };

  return { searchValue, options, handleSearch, setSearchValue };
}
